import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { MediaFolder } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteMediaFolder } from '../hooks/use-media-folder-mutations';

export interface DeleteFolderDialogProps {
  /** Null keeps the dialog closed. */
  folder: MediaFolder | null;
  onOpenChange: (open: boolean) => void;
  onDeleted?: (id: string) => void;
}

/** Deleting a folder only drops the grouping; it says so before it asks. */
export function DeleteFolderDialog({ folder, onOpenChange, onDeleted }: DeleteFolderDialogProps) {
  const { t } = useTranslation(['media', 'common']);
  const remove = useDeleteMediaFolder();
  const message = useErrorMessage();

  return (
    <AlertDialog open={folder !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('media:folders.deleteTitle', { name: folder?.name ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('media:folders.deleteDescription', { count: folder?.itemCount ?? 0 })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.error ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{message(remove.error)}</AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (!folder) return;
              remove.mutate(folder.id, {
                onSuccess: () => {
                  onDeleted?.(folder.id);
                  onOpenChange(false);
                },
              });
            }}
          >
            {t('common:actions.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
